/**
 * Pure helper functions for RecordPage step indicator logic.
 *
 * Extracted for testability — all functions are pure (no React or DOM).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type InputMode = 'voice' | 'text' | 'edit';

export type ProcessingStep =
  | 'idle'          // waiting for user input
  | 'transcribing'  // uploading + Whisper transcription
  | 'extracting'    // LLM extraction
  | 'done';         // navigating to review

export interface StepDef {
  key: string;
  label: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const STEPS: readonly StepDef[] = [
  { key: 'input', label: 'Record' },
  { key: 'transcribing', label: 'Transcribe' },
  { key: 'extracting', label: 'Extract' },
  { key: 'done', label: 'Review' },
] as const;

// ---------------------------------------------------------------------------
// Functions
// ---------------------------------------------------------------------------

/**
 * Given the current processing step and input mode, return the index into
 * the *full* STEPS array that is currently active.
 */
export function getActiveStepIndex(step: ProcessingStep, mode: InputMode): number {
  switch (step) {
    case 'idle': return 0;
    case 'transcribing': return mode === 'text' ? 2 : 1; // text skips transcribe
    case 'extracting': return 2;
    case 'done': return 3;
  }
}

/**
 * Build the list of visible steps for the given input mode.
 *
 * In text mode:
 *   - The "Transcribe" step is removed (text input skips transcription)
 *   - The first step label changes from "Record" to "Type"
 *
 * Returns an array of { key, label } objects representing only the steps
 * that should be rendered in the UI.
 */
export function getVisibleSteps(mode: InputMode): StepDef[] {
  return STEPS
    .filter((step) => !(mode === 'text' && step.key === 'transcribing'))
    .map((step) => ({
      ...step,
      label: mode === 'text' && step.key === 'input' ? 'Type' : step.label,
    }));
}
